/**
 * Subformula helpers — id generation + master AST sync
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FormulaBlockNode, FormulaSubformula } from '@trafficpulse/shared';
import { serializeBlock } from '@trafficpulse/shared';

let counter = 0;
export function generateSubId(): string {
  return `sf_${Date.now().toString(36)}_${counter++}`;
}

export function makeSubformula(name: string): FormulaSubformula {
  return {
    id: generateSubId(),
    name,
    description: '',
    ast: null,
    expression: '',
  };
}

/** Append a subref to the master AST as a `+` term. Empty master becomes the subref alone. */
export function appendSubrefToMaster(
  master: FormulaBlockNode | null,
  subId: string,
): FormulaBlockNode {
  const ref: FormulaBlockNode = { kind: 'subref', subId };
  if (!master) return ref;
  return { kind: 'binary', op: '+', left: master, right: ref };
}

/** Remove every subref to `subId` from the master AST. Returns null when nothing remains. */
export function pruneSubrefFromMaster(
  master: FormulaBlockNode | null,
  subId: string,
): FormulaBlockNode | null {
  if (!master || typeof master !== 'object' || !('kind' in master)) return null;
  if (master.kind === 'subref') return master.subId === subId ? null : master;
  if (master.kind === 'metric' || master.kind === 'num') return master;
  if (master.kind === 'unary') {
    if (!('operand' in master)) return null;
    const operand = pruneSubrefFromMaster(master.operand, subId);
    return operand ? { kind: 'unary', op: '-', operand } : null;
  }
  if (master.kind === 'call') {
    if (!Array.isArray(master.args)) return null;
    const args = master.args
      .map((a) => pruneSubrefFromMaster(a, subId))
      .filter((a): a is FormulaBlockNode => a !== null);
    return args.length === 0 ? null : { kind: 'call', fn: master.fn, args };
  }
  if (master.kind === 'binary') {
    if (!('left' in master) || !('right' in master) || !('op' in master)) return null;
    const left = pruneSubrefFromMaster(master.left, subId);
    const right = pruneSubrefFromMaster(master.right, subId);
    if (left && right) return { kind: 'binary', op: master.op, left, right };
    return left ?? right ?? null;
  }
  return null;
}

export function serializeMaster(master: FormulaBlockNode | null): string {
  return serializeBlock(master);
}
